// src/components/UI/EntityTranslationsButton.jsx
//
// Reusable "add an alternate name in another language" control for the
// generic entity-translation feature (see EntityTranslation's own doc
// comment in internal/models/language.go, and
// internal/handlers/entity_translation_handler.go). Drop this next to any
// Add/Edit page's Save/Cancel row once the record has a real ID — a
// Country, a Category, a Product, a Branch, the system name, whatever
// `entityTranslationTables` on the backend whitelists — and it handles the
// rest: loading every active language, showing what's already saved for
// each, editing one language at a time, and saving them all back together.
//
// Deliberately renders nothing (returns null) before the record has an id
// yet (a brand-new "Add" page) — there is nothing to attach a translation
// to before the row itself is created — and before the record has an id
// yet, and for a user without language_view, so it's always safe to render
// this unconditionally next to a Save button rather than every caller
// having to guard it itself.
import { useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import { getLanguages, getEntityTranslations, saveEntityTranslations } from "../../services/api";
import { notify } from "../../utils/notify";
import { ICONS } from "../../icons";
import Button from "./Button";

export default function EntityTranslationsButton({ entityType, entityId, defaultName, label, size = "input" }) {
  const token = localStorage.getItem("token");
  const { userPermissions } = useContext(AuthContext);
  const canView = userPermissions?.includes("language_view");
  const canEdit = userPermissions?.includes("language_edit");

  const [isOpen, setIsOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [draft, setDraft] = useState({}); // language code -> alternate text
  const [activeCode, setActiveCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!canView || !entityId) return null;

  const open = async () => {
    setIsOpen(true);
    setLoading(true);
    try {
      const [langs, existing] = await Promise.all([
        getLanguages(token),
        getEntityTranslations(token, entityType, entityId),
      ]);
      setLanguages(langs || []);
      setDraft({ ...(existing?.translations || {}) });
      setActiveCode(langs?.[0]?.code || null);
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to load translations");
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (saving) return;
    setIsOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveEntityTranslations(token, entityType, entityId, draft);
      notify.success("Translations saved");
      setIsOpen(false);
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to save translations");
    } finally {
      setSaving(false);
    }
  };

  return (
    // mb-1 matches FloatingInput/SelectBox's own outer wrapper margin
    // exactly, so a parent aligning this button against one of those
    // fields with "items-end" lines up the two bottom edges precisely
    // instead of this button sitting a few pixels lower.
    <div className="mb-1">
      <Button type="button" variant="outline" size={size} icon={ICONS.Translate} onClick={open}>
        {label || "Translations"}
      </Button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 15 }}
              className="relative bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl z-10"
            >
              <h3 className="text-xl font-black text-gray-900 mb-1">Alternate Names</h3>
              <p className="text-gray-400 text-sm mb-6">
                {defaultName && (
                  <>Original: <span className="font-bold text-gray-600">{defaultName}</span>. </>
                )}
                Leave a language blank to fall back to the original.
              </p>

              {loading ? (
                <div className="text-center text-gray-400 py-10 animate-pulse">Loading...</div>
              ) : languages.length === 0 ? (
                <div className="text-center text-gray-400 py-10 italic">
                  No additional languages yet — add one from the Languages page first.
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => setActiveCode(l.code)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wide transition-colors ${
                          activeCode === l.code
                            ? "bg-[var(--primary-color)] text-white shadow-sm"
                            : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {l.name}
                        {draft[l.code] ? " •" : ""}
                      </button>
                    ))}
                  </div>

                  <textarea
                    disabled={!canEdit}
                    value={draft[activeCode] ?? ""}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [activeCode]: e.target.value }))}
                    placeholder={defaultName || ""}
                    rows={3}
                    className="w-full text-sm text-gray-900 bg-gray-50/50 rounded-xl border-2 border-gray-100 focus:border-[var(--primary-color)] transition-all outline-none focus:outline-none focus:ring-0 px-4 py-2 disabled:opacity-60"
                  />
                </>
              )}

              <div className="flex gap-3 mt-8">
                <Button variant="secondary" onClick={close} className="flex-1 !rounded-2xl" disabled={saving}>
                  Cancel
                </Button>
                {canEdit && (
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    isLoading={saving}
                    className="flex-1 !rounded-2xl"
                    disabled={loading || languages.length === 0}
                  >
                    Save
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
